
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, forwardRef, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, Input } from "@angular/core";
import { TaskOptionComponent } from "../task-option.component";
import { Project, Tag, ICompact, User } from "../../../../models";
import { Store } from "@ngrx/store";
import { TasksRootState, getFolderList, getMemberList } from "../../../../store/index";
import { Subject } from "rxjs";
import { TaskRepository } from "../../../../repository/task.repository";
import { MessageTranslatorService } from "../../../../services";
import { TasksConstants } from "../../../../shared/task-constacts";
import { debounceTime, takeWhile } from "rxjs/operators";
import { getOnlineStatus } from "src/app/reducers";
import { Broadcaster } from "src/app/common/providers/broadcaster.service";
import sort from "fast-sort";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "vp-vnctask-option-add-watchers",
  providers: [{
    provide: TaskOptionComponent,
    // eslint-disable-next-line
    useExisting: forwardRef(() => TaskAddWatchersOptionComponent)
  }],
  templateUrl: "option-add-watchers.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskAddWatchersOptionComponent extends TaskOptionComponent implements OnInit, OnDestroy {
  id: string = "add-watchers";
  triggerKey = "&";
  @Input() project: Project;
  isAlive = true;
  isOnline: boolean = false;
  selectedMemberList: User[] = [];
  memberList: User[] = [];
  defaultMemberList: User[] = [];
  isUpdated: boolean = false;
  constructor(
    private store: Store<TasksRootState>,
    private taskRepo: TaskRepository,
    private broadcaster: Broadcaster,
    public changerDetectorRef: ChangeDetectorRef,
    private messageTranslatorService: MessageTranslatorService,
    public matDialog: MatDialog
  ) {
    super(changerDetectorRef, matDialog);

  }

  ngOnInit() {
    this.store.select(getMemberList).pipe(takeWhile(() => this.isAlive)).subscribe( members => {
      if (members) {
        members.forEach( member => (<any>member).name = member ? member.name : "");
        this.memberList = members;
        this.defaultMemberList = members;
        this.setItems(members);
        this.changerDetectorRef.markForCheck();
      }
    });

    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });

    this.broadcaster.on<any>("sortMemberList").pipe(takeWhile(() => this.isAlive))
    .subscribe(presence => {
      if (this.memberList) {
        if (this.memberList.length > 0) {
          sort(this.memberList).asc( member => member.name);
        }
      }
    });
  }

  ngOnChanges(changes) {
    console.log("TaskAddWatchersOptionComponent ngOnChanges", changes);
    if (this.defaultMemberList && this.defaultMemberList.length > 0) {
      let watcherValue = this.getWatcherValue();
      this.memberList = this.defaultMemberList.filter( member => !watcherValue.includes(member));
      this.setItems(this.memberList);
    }
    if (changes && changes.project && changes.project.currentValue) {
      if (this.isOnline) {
          this.taskRepo.getMemberList(changes.project.currentValue.id);
      }
      this.changerDetectorRef.markForCheck();
    }
  }

  validate() {
    if (!this.isValid()) {
      this.taskRepo.errorProjectRequired();
    }
  }

  isValid() {
    return this.project && typeof this.project.id === "number";
  }

  getPayload() {
    let value = this.getWatcherValue();

    console.log("[TaskAddWatchersOptionComponent][getPayload] value", value);

    if (!value) return { };
    return { watchers: value };
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  addMemberToList(member) {
    this.selectedMemberList.push(member);
    this.memberList = this.memberList.filter(item => item.id !== member.id);
    this.setItems(this.memberList);
    this.isUpdated = true;
    this.changerDetectorRef.markForCheck();
  }

  removeSelectedMember(member) {
    this.selectedMemberList = this.selectedMemberList.filter(item => item.id !== member.id);
    this.memberList.push(member);
    this.broadcaster.broadcast("sortMemberList");
    this.setItems(this.memberList);
    this.isUpdated = true;
    if (this.selectedMemberList && this.selectedMemberList.length === 0) {
      this.isUpdated = false;
    }
    this.changerDetectorRef.markForCheck();
  }

  backToMain() {
    this.resetWatchersList();
    this.goBack();
  }

  resetWatchersList() {
    this.selectedMemberList = [];
    this.changerDetectorRef.markForCheck();
  }

  setWatchers() {
    console.log("[TaskAddWatchersOptionComponent][setWatchers] setWatchers", this.selectedMemberList);

    this.onWatcherClick(this.selectedMemberList);
    this.resetWatchersList();
  }

  removeWatcher(userName) {
    this.onRemoveWatcher(userName);
    let watcherValue = this.getWatcherValue();
    this.memberList = this.defaultMemberList.filter( member => !watcherValue.includes(member));
    this.setItems(this.memberList);
  }
}
